import { isR2ImageBase, r2VariantUrl, r2SrcSet, R2_IMAGE_WIDTHS, R2_FALLBACK_WIDTH } from './images';

export const uploadToCloudinary = async (file: File): Promise<string> => {
  const cloudName = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME;
  const uploadPreset = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET;

  if (!cloudName || !uploadPreset) {
    throw new Error('Cloudinary configuration is missing');
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', uploadPreset);

  try {
    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
      {
        method: 'POST',
        body: formData,
      }
    );

    if (!response.ok) {
      throw new Error('Failed to upload image to Cloudinary');
    }

    const data = await response.json();
    return data.secure_url;
  } catch (error) {
    console.error('Cloudinary upload error:', error);
    throw error;
  }
};

// Picks the smallest pre-generated width that still covers what was asked for.
const nearestR2Width = (width?: number): number => {
  if (!width) return R2_FALLBACK_WIDTH;
  return R2_IMAGE_WIDTHS.find((w) => w >= width) ?? R2_IMAGE_WIDTHS[R2_IMAGE_WIDTHS.length - 1];
};

export const getOptimizedImageUrl = (url: string, width?: number, height?: number): string => {
  // R2-backed images are pre-generated rather than transformed on the fly.
  // WebP is used here (96% support) because this returns a single URL with no
  // room to negotiate; ResponsiveImage is what unlocks AVIF.
  if (isR2ImageBase(url)) {
    return r2VariantUrl(url, nearestR2Width(width), 'webp');
  }

  if (!url.includes('cloudinary.com')) {
    return url;
  }

  const transformations = [];
  if (width) transformations.push(`w_${width}`);
  if (height) transformations.push(`h_${height}`);
  transformations.push('c_fill', 'f_auto', 'q_auto');

  const parts = url.split('/upload/');
  if (parts.length !== 2) return url;

  return `${parts[0]}/upload/${transformations.join(',')}/${parts[1]}`;
};

export const getResponsiveSrcSet = (
  url: string,
  widths: number[],
  aspectRatio?: number,
): string => {
  // R2 variants come from a fixed ladder, so the caller's requested widths
  // don't apply — hand back the ladder we actually generated.
  if (isR2ImageBase(url)) return r2SrcSet(url, 'webp');

  if (!widths.length) return '';

  return widths
    .map((width) => {
      const height = aspectRatio ? Math.round(width * aspectRatio) : undefined;
      return `${getOptimizedImageUrl(url, width, height)} ${width}w`;
    })
    .join(', ');
};